import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { useFeature } from '@/hooks/use-feature';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

type ShopifyMutationResponse = {
  syncShopifyData: string;
};

type KlaviyoMutationResponse = {
  syncKlaviyoData: string;
};

const SYNC_SHOPIFY_DATA = gql`
  mutation SyncShopifyData($merchantId: String!, $shopUrl: String!, $accessToken: String!) {
    syncShopifyData(merchant_id: $merchantId, shop_url: $shopUrl, access_token: $accessToken)
  }
`;

const SYNC_KLAVIYO_DATA = gql`
  mutation SyncKlaviyoData($merchantId: String!, $apiKey: String!) {
    syncKlaviyoData(merchant_id: $merchantId, api_key: $apiKey)
  }
`;

function ShopifySync() {
  const { toast } = useToast();
  const [inputs, setInputs] = useState({
    merchantId: '',
    shopUrl: '',
    accessToken: ''
  });

  const [syncShopify, { loading }] = useMutation<ShopifyMutationResponse>(SYNC_SHOPIFY_DATA);

  const handleSync = async () => {
    if (!inputs.merchantId || !inputs.shopUrl || !inputs.accessToken) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all Shopify fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const result = await syncShopify({
        variables: inputs
      });

      if (result.data) {
        toast({
          title: 'Shopify Sync Started',
          description: `Workflow ID: ${result.data.syncShopifyData}`
        });
      }
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shopify Sync</CardTitle>
        <CardDescription>Sync data from your Shopify store</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="Merchant ID"
          value={inputs.merchantId}
          onChange={(e) => {
            setInputs((prev) => ({ ...prev, merchantId: e.target.value }));
          }}
        />
        <Input
          placeholder="Shop URL"
          value={inputs.shopUrl}
          onChange={(e) => {
            setInputs((prev) => ({ ...prev, shopUrl: e.target.value }));
          }}
        />
        <Input
          placeholder="Access Token"
          type="password"
          value={inputs.accessToken}
          onChange={(e) => {
            setInputs((prev) => ({ ...prev, accessToken: e.target.value }));
          }}
        />
        <Button
          onClick={() => {
            void handleSync();
          }}
          disabled={loading}
          className="w-full"
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {loading ? 'Starting Sync...' : 'Sync Shopify Data'}
        </Button>
      </CardContent>
    </Card>
  );
}

function KlaviyoSync() {
  const { toast } = useToast();
  const [inputs, setInputs] = useState({
    merchantId: '',
    apiKey: ''
  });

  const [syncKlaviyo, { loading }] = useMutation<KlaviyoMutationResponse>(SYNC_KLAVIYO_DATA);

  const handleSync = async () => {
    if (!inputs.merchantId || !inputs.apiKey) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all Klaviyo fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const result = await syncKlaviyo({
        variables: inputs
      });

      if (result.data) {
        toast({
          title: 'Klaviyo Sync Started',
          description: `Workflow ID: ${result.data.syncKlaviyoData}`
        });
      }
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Klaviyo Sync</CardTitle>
        <CardDescription>Sync data from your Klaviyo account</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="Merchant ID"
          value={inputs.merchantId}
          onChange={(e) => {
            setInputs((prev) => ({ ...prev, merchantId: e.target.value }));
          }}
        />
        <Input
          placeholder="API Key"
          type="password"
          value={inputs.apiKey}
          onChange={(e) => {
            setInputs((prev) => ({ ...prev, apiKey: e.target.value }));
          }}
        />
        <Button
          onClick={() => {
            void handleSync();
          }}
          disabled={loading}
          className="w-full"
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {loading ? 'Starting Sync...' : 'Sync Klaviyo Data'}
        </Button>
      </CardContent>
    </Card>
  );
}

export function AdminSettings() {
  const isEnabled = useFeature('ADMIN_SETTINGS');

  if (!isEnabled) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Admin Tools</h3>
        <p className="text-sm text-muted-foreground">
          Internal tools for data management and synchronization.
        </p>
      </div>
      <div className="grid gap-6">
        <ShopifySync />
        <KlaviyoSync />
      </div>
    </div>
  );
}
